<template>
  <div class="fence-tutorial-page">
    <!-- Header -->
    <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-2">
      <div>
        <div class="d-flex align-items-center mb-25">
          <b-button
            variant="flat-primary"
            size="sm"
            class="btn-icon mr-50 p-25"
            :to="{ name: 'fence-management' }"
            v-b-tooltip.hover
            :title="$t('Voltar')"
          >
            <feather-icon icon="ArrowLeftIcon" size="18" />
          </b-button>
          <h3 class="font-weight-bolder text-primary mb-0 d-flex align-items-center">
            <feather-icon icon="BookOpenIcon" size="24" class="mr-50" />
            {{ $t('Tutorial de Cercas Virtuais') }}
          </h3>
        </div>
        <p class="text-muted font-small-3 mb-0 ml-2">
          {{ $t('Aprenda a gerenciar, interpretar alertas e utilizar todos os recursos do módulo de cercas.') }}
        </p>
      </div>
    </div>

    <b-row class="match-height">
      <!-- Seção 1: Visão Geral e Gerenciamento -->
      <b-col cols="12" md="6">
        <b-card class="shadow-sm">
          <b-card-title class="d-flex align-items-center">
            <div class="tutorial-icon-wrapper bg-light-primary text-primary mr-1">
              <feather-icon icon="MapIcon" size="20" />
            </div>
            {{ $t('Visão Geral e Modos de Visualização') }}
          </b-card-title>
          <b-card-body class="px-0 pb-0">
            <p class="text-muted mb-2">
              {{ $t('Você pode gerenciar suas cercas virtuais alternando entre dois modos práticos:') }}
            </p>
            <div class="d-flex align-items-start mb-1">
              <feather-icon icon="MapIcon" size="18" class="text-primary mt-25 mr-50" />
              <div>
                <strong class="text-body">{{ $t('Visualização em Mapa') }}</strong>
                <p class="font-small-3 text-muted mb-0">{{ $t('Ideal para ver a posição geográfica exata de cada cerca. Utiliza uma gaveta lateral para exibir a listagem em cards, que agora conta com paginação para melhor performance em grandes volumes.') }}</p>
              </div>
            </div>
            <div class="d-flex align-items-start">
              <feather-icon icon="ListIcon" size="18" class="text-primary mt-25 mr-50" />
              <div>
                <strong class="text-body">{{ $t('Visualização em Tabela') }}</strong>
                <p class="font-small-3 text-muted mb-0">{{ $t('Perfeita para visualizar os dados de forma tabular e filtrar múltiplas cercas rapidamente.') }}</p>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Seção 2: Entendendo os Ícones e Alertas -->
      <b-col cols="12" md="6">
        <b-card class="shadow-sm">
          <b-card-title class="d-flex align-items-center">
            <div class="tutorial-icon-wrapper bg-light-warning text-warning mr-1">
              <feather-icon icon="BellIcon" size="20" />
            </div>
            {{ $t('Tipos de Alertas') }}
          </b-card-title>
          <b-card-body class="px-0 pb-0">
            <p class="text-muted mb-2">
              {{ $t('Cada cerca pode ser configurada para disparar alertas quando um veículo interage com ela. Nos cards, usamos ícones específicos:') }}
            </p>
            <b-row>
              <b-col cols="6" class="mb-1">
                <div class="d-flex align-items-center">
                  <div class="fence-alert-chip fence-alert-chip--active d-flex align-items-center justify-content-center mr-50">
                    <feather-icon icon="LogInIcon" size="14" />
                  </div>
                  <span class="font-weight-bold font-small-3">{{ $t('Entrada (In)') }}</span>
                </div>
                <small class="text-muted d-block mt-25">{{ $t('Dispara quando o veículo entra na cerca.') }}</small>
              </b-col>
              <b-col cols="6" class="mb-1">
                <div class="d-flex align-items-center">
                  <div class="fence-alert-chip fence-alert-chip--active d-flex align-items-center justify-content-center mr-50">
                    <feather-icon icon="LogOutIcon" size="14" />
                  </div>
                  <span class="font-weight-bold font-small-3">{{ $t('Saída (Out)') }}</span>
                </div>
                <small class="text-muted d-block mt-25">{{ $t('Dispara quando o veículo sai da cerca.') }}</small>
              </b-col>
              <b-col cols="6" class="mb-1">
                <div class="d-flex align-items-center">
                  <div class="fence-alert-chip fence-alert-chip--active d-flex align-items-center justify-content-center mr-50">
                    <feather-icon icon="Minimize2Icon" size="14" />
                  </div>
                  <span class="font-weight-bold font-small-3">{{ $t('Permanece Dentro') }}</span>
                </div>
                <small class="text-muted d-block mt-25">{{ $t('Dispara se ficar X minutos dentro.') }}</small>
              </b-col>
              <b-col cols="6" class="mb-1">
                <div class="d-flex align-items-center">
                  <div class="fence-alert-chip fence-alert-chip--active d-flex align-items-center justify-content-center mr-50">
                    <feather-icon icon="Maximize2Icon" size="14" />
                  </div>
                  <span class="font-weight-bold font-small-3">{{ $t('Permanece Fora') }}</span>
                </div>
                <small class="text-muted d-block mt-25">{{ $t('Dispara se ficar X minutos fora.') }}</small>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Seção 3: Como Ler os Cards e Status -->
      <b-col cols="12">
        <b-card class="shadow-sm mt-1">
          <b-card-title class="d-flex align-items-center">
            <div class="tutorial-icon-wrapper bg-light-success text-success mr-1">
              <feather-icon icon="CheckCircleIcon" size="20" />
            </div>
            {{ $t('Status de Alerta nos Cards (Ativo vs Inativo)') }}
          </b-card-title>
          <b-card-body class="px-0 pb-0">
            <p class="text-muted mb-2">
              {{ $t('Nos cards de visualização no mapa, a cor do ícone de alerta determina rapidamente se aquela função está ativada.') }}
            </p>
            <b-row class="text-center">
              <b-col md="6" class="mb-2 mb-md-0 border-right">
                <h6 class="font-weight-bolder text-success mb-1">{{ $t('Alerta Ativo (Verde)') }}</h6>
                <div class="fence-alert-chip fence-alert-chip--active d-inline-flex align-items-center justify-content-center mb-1" style="width: 48px; height: 48px;">
                  <feather-icon icon="LogInIcon" size="24" />
                </div>
                <p class="font-small-3 text-muted px-2">
                  <span v-html="$t('Quando o fundo é esverdeado e o ícone ganha destaque, significa que a cerca está configurada para <strong>emitir notificações</strong> para esse evento.')"></span>
                </p>
              </b-col>
              <b-col md="6">
                <h6 class="font-weight-bolder text-secondary mb-1">{{ $t('Alerta Inativo (Cinza)') }}</h6>
                <div class="fence-alert-chip fence-alert-chip--inactive d-inline-flex align-items-center justify-content-center mb-1" style="width: 48px; height: 48px;">
                  <feather-icon icon="LogInIcon" size="24" />
                </div>
                <p class="font-small-3 text-muted px-2">
                  <span v-html="$t('Quando o fundo é cinza, a funcionalidade daquele alerta está <strong>desligada</strong> para a cerca específica.')"></span>
                </p>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Seção 4: Ações Rápidas -->
      <b-col cols="12">
        <b-card class="shadow-sm mt-1">
          <b-card-title class="d-flex align-items-center">
            <div class="tutorial-icon-wrapper bg-light-info text-info mr-1">
              <feather-icon icon="SettingsIcon" size="20" />
            </div>
            {{ $t('Ações Rápidas') }}
          </b-card-title>
          <b-card-body class="px-0 pb-0">
            <p class="text-muted mb-2">{{ $t('Na parte inferior de cada card, você encontra botões rápidos para gestão:') }}</p>
            <div class="d-flex flex-wrap align-items-center" style="gap: 16px;">
              <div class="d-flex align-items-center bg-light rounded p-1">
                <b-button variant="flat-primary" size="sm" class="p-25 d-inline-flex align-items-center font-small-1 mr-50">
                  <feather-icon icon="CrosshairIcon" size="13" class="mr-25" /> {{ $t('Focar') }}
                </b-button>
                <span class="font-small-3 text-muted">{{ $t('Ajusta o mapa centralizando na cerca.') }}</span>
              </div>
              <div class="d-flex align-items-center bg-light rounded p-1">
                <b-button variant="flat-info" size="sm" class="p-25 d-inline-flex align-items-center font-small-1 mr-50">
                  <feather-icon icon="Edit2Icon" size="13" class="mr-25" /> {{ $t('Editar') }}
                </b-button>
                <span class="font-small-3 text-muted">{{ $t('Abre a tela de edição das configurações.') }}</span>
              </div>
              <div class="d-flex align-items-center bg-light rounded p-1">
                <b-button variant="flat-danger" size="sm" class="p-25 d-inline-flex align-items-center font-small-1 mr-50">
                  <feather-icon icon="Trash2Icon" size="13" class="mr-25" /> {{ $t('Excluir') }}
                </b-button>
                <span class="font-small-3 text-muted">{{ $t('Remove permanentemente a cerca (pede confirmação).') }}</span>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <!-- Seção 5: Como Criar e Desenhar uma Cerca -->
      <b-col cols="12">
        <b-card class="shadow-sm mt-1">
          <b-card-title class="d-flex align-items-center">
            <div class="tutorial-icon-wrapper bg-light-primary text-primary mr-1">
              <feather-icon icon="PenToolIcon" size="20" />
            </div>
            {{ $t('Passo a Passo: Como Criar uma Cerca Virtual') }}
          </b-card-title>
          <b-card-body class="px-0 pb-0">
            <p class="text-muted mb-2">
              {{ $t('Criar uma cerca é rápido e totalmente visual. Siga os passos abaixo para delimitar suas áreas de interesse no mapa:') }}
            </p>
            
            <b-row>
              <!-- Passo 1 -->
              <b-col md="6" lg="3" class="mb-2">
                <div class="d-flex align-items-start">
                  <div class="tutorial-icon-wrapper bg-primary text-white mr-1 flex-shrink-0" style="width: 32px; height: 32px; font-weight: bold;">
                    1
                  </div>
                  <div>
                    <h6 class="font-weight-bolder mb-50">{{ $t('Iniciar Criação') }}</h6>
                    <p class="font-small-3 text-muted mb-0">
                      {{ $t('Na tela de Gestão, clique no botão azul "Criar Nova Cerca" localizado no canto superior direito.') }}
                    </p>
                  </div>
                </div>
              </b-col>

              <!-- Passo 2 -->
              <b-col md="6" lg="3" class="mb-2">
                <div class="d-flex align-items-start">
                  <div class="tutorial-icon-wrapper bg-primary text-white mr-1 flex-shrink-0" style="width: 32px; height: 32px; font-weight: bold;">
                    2
                  </div>
                  <div>
                    <h6 class="font-weight-bolder mb-50">{{ $t('Escolher Formato no Mapa') }}</h6>
                    <p class="font-small-3 text-muted mb-0">
                      <span v-html="$t('Procure pela <strong>barra de ferramentas</strong> no canto esquerdo do mapa.')"></span><br>
                      <span class="d-inline-flex align-items-center mt-50 mb-25">
                        <feather-icon icon="HexagonIcon" size="12" class="mr-25 text-primary" /> <strong>{{ $t('Polígono:') }}</strong>
                      </span> {{ $t('forma livre.') }}<br>
                      <span class="d-inline-flex align-items-center">
                        <feather-icon icon="CircleIcon" size="12" class="mr-25 text-primary" /> <strong>{{ $t('Círculo:') }}</strong>
                      </span> {{ $t('raio perfeito.') }}
                    </p>
                  </div>
                </div>
              </b-col>

              <!-- Passo 3 -->
              <b-col md="6" lg="3" class="mb-2">
                <div class="d-flex align-items-start">
                  <div class="tutorial-icon-wrapper bg-primary text-white mr-1 flex-shrink-0" style="width: 32px; height: 32px; font-weight: bold;">
                    3
                  </div>
                  <div>
                    <h6 class="font-weight-bolder mb-50">{{ $t('Desenhar no Mapa') }}</h6>
                    <p class="font-small-3 text-muted mb-0" style="line-height: 1.4;">
                      <span v-html="$t('Após clicar no ícone desejado na barra do mapa:')"></span><br>
                      <strong>{{ $t('Polígono:') }}</strong> {{ $t('Clique no mapa para criar as pontas e clique de volta no 1º ponto para fechar.') }}<br>
                      <strong>{{ $t('Círculo:') }}</strong> {{ $t('Clique no centro desejado e arraste o mouse.') }}
                    </p>
                  </div>
                </div>
              </b-col>

              <!-- Passo 4 -->
              <b-col md="6" lg="3" class="mb-2">
                <div class="d-flex align-items-start">
                  <div class="tutorial-icon-wrapper bg-primary text-white mr-1 flex-shrink-0" style="width: 32px; height: 32px; font-weight: bold;">
                    4
                  </div>
                  <div>
                    <h6 class="font-weight-bolder mb-50">{{ $t('Configurar e Salvar') }}</h6>
                    <p class="font-small-3 text-muted mb-0">
                      {{ $t('Preencha os dados e ligue os alarmes desejados (Entrada, Saída, etc).') }}
                      <span class="d-block mt-50 p-50 bg-light-success text-success font-weight-bold rounded text-center border border-success">
                        <feather-icon icon="SaveIcon" size="14" class="mr-25" />
                        {{ $t('É necessário clicar em "Salvar Cerca" para finalizar!') }}
                      </span>
                    </p>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BRow, BCol, BCard, BCardTitle, BCardBody, BButton, VBTooltip } from 'bootstrap-vue'

export default {
  name: 'FenceTutorial',
  components: {
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BCardBody,
    BButton,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
}
</script>

<style lang="scss" scoped>
.fence-tutorial-page {
  padding-bottom: 2rem;
}

.tutorial-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 8px;
}

.fence-alert-chip {
  padding: 6px;
  border-radius: 6px;
  
  &--active {
    background-color: rgba(40, 199, 111, 0.15);
    color: #28c76f;
  }

  &--inactive {
    background-color: #ededf2;
    color: #b0b2c0;
  }
}
</style>
